// flow

import * as React from 'react';
import R from 'ramda';
import moment from 'moment';

import { getPlatforms } from '../RuleUtils';

import type { Rule, RuleQueryData } from '../../../../types/journeyTypes';

import { FILTERS } from '../journeyText/audienceEditor';
import {
  DATE_NEW_JOURNEY_FIELDS_ADDED,
  QUERY_BUILDER_TYPES
} from '../journeyConstants';

export * from './deviceUtils';
export * from './scheduleUtils';
export * from './tagUtils';
export * from './templateUtils';
export * from './validationUtils';

/*
 * The Journey ID can be parsed into a timestamp
 *
 * NOTE: For Journeys created before DATE_NEW_JOURNEY_FIELDS_ADDED,
 * this ID will not be equivalent to creation date, as it did not have an
 * ID when it was created (they were all added DATE_NEW_JOURNEY_FIELDS_ADDED)
 *
 * We are not worrying about time zone here because we don't need to be that
 * precise.
 */
const getJourneyCreationDate = (journeyId: string) => {
  return new Date(parseInt(journeyId.substring(0, 8), 16) * 1000);
};

/* We added new EO parameters to Journeys around DATE_NEW_JOURNEY_FIELDS_ADDED
 *
 * NOTE: We are using creation date as a proxy for "may have data before this
 * legacy trasition date", as actually querying Druid to see if the Journey
 * has data from before than would be not worth the effort at this point.
 */
export const journeyMayHaveLegacyData = (journeyId: string) => {
  const creationDate = getJourneyCreationDate(journeyId);
  return moment(creationDate).isBefore(DATE_NEW_JOURNEY_FIELDS_ADDED);
};

const getJourneysByPlatforms = (
  journeys: Array<Rule>,
  platforms: RuleQueryData.platform
): Array<Rule> => {
  return journeys.filter(journey => {
    const commonPlatforms = R.intersection(getPlatforms(journey), platforms);
    return !!commonPlatforms.length;
  });
};

const formatJourneysForDropdown = (
  journeys: Array<Rule>
): Array<{ value: string, label: string }> => {
  return journeys.map((journey: Rule) => ({
    label: journey.title,
    value: journey.id
  }));
};

export const filterJourneysByCompatiblePlatforms = (
  journeys: Array<Rule>,
  journeyToMatch: Rule
): Array<{ value: string, label: string }> => {
  const platformCompatibleJourneys = getJourneysByPlatforms(
    journeys,
    getPlatforms(journeyToMatch)
  );
  return formatJourneysForDropdown(platformCompatibleJourneys);
};

export const getQuantifier = (
  quantifierKey: 'DAY' | 'TIME',
  quantity: number
) => {
  const quantityType = FILTERS.QUANTIFIERS[quantifierKey];
  return quantity === 1 ? quantityType.SINGULAR : quantityType.PLURAL;
};

// If there is a 'view_dismiss' filter saved with the correct viewId,
// extract the dismissPeriod from that filter
export const getDismissPeriodFromFilter = (
  viewId: string,
  rule: Rule
): ?number => {
  if (rule) {
    const ruleData = rule.builder.getData();
    const viewDismissData = R.find(
      filter =>
        R.propEq('option', QUERY_BUILDER_TYPES.VIEW_DISMISS)(filter) &&
        R.propEq('viewId', viewId)(filter)
    )(ruleData.filters);
    if (viewDismissData && viewDismissData.range) {
      // The 'range' property for a 'view_dismiss' filter will always be
      // an array with only one value;
      return viewDismissData.range[0];
    }
  }
  return null;
};

// This is to create the filter to save to the rule
export const createDismissFilter = (
  dismissPeriod: number,
  viewId: string
): {
  operator: number,
  option: string,
  range: [number],
  viewId: string
} => {
  const operator =
    dismissPeriod < 0
      ? QUERY_BUILDER_TYPES.NO_BOUNDS
      : QUERY_BUILDER_TYPES.VALUE_LESS_THAN;
  return {
    operator,
    option: QUERY_BUILDER_TYPES.VIEW_DISMISS,
    range: [dismissPeriod],
    viewId
  };
};
